import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import {
  Toolbar,
  Typography,
  Container,
  Box,
  Link,
  Divider,
  Button,
  Alert as MUIAlert,
  AlertTitle,
  Paper,
  CircularProgress,
  TextField,
  IconButton,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import { googleLogout } from '@react-oauth/google';
// import ApiStatus from './components/ApiStatus'; // Commented out
import LoginButton from './components/LoginButton';
import NotificationSnackbar from './components/NotificationSnackbar';
import type { AlertColor } from '@mui/material/Alert';
import axios from 'axios'; // For beta request API call
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ReferralLinks from './components/ReferralLinks'; // Import the new component
import UsageDisplay from './components/UsageDisplay'; // Import the UsageDisplay component
// import TotalUsageDisplay from './components/TotalUsageDisplay'; // Comment out or remove TotalUsageDisplay
import SubscriptionStatusDisplay from './components/SubscriptionStatusDisplay'; // Import the new component
import { loadStripe } from '@stripe/stripe-js'; // Import loadStripe
import type { Stripe } from '@stripe/stripe-js'; // Type-only import for Stripe
import PaymentSuccessPage from './pages/PaymentSuccessPage'; // Import new page
import PaymentCancelledPage from './pages/PaymentCancelledPage'; // Import new page
import {
  API_BASE_URL,
  BETA_REQUEST_URL,
  STRIPE_PUBLISHABLE_KEY,
  REFERRAL_COOKIE_NAME
} from './config'; // Import centralized constants
import AnalyticsTracker from './components/AnalyticsTracker';

// Define a type for your user object for better type safety
interface AppUser {
  id: string;
  googleId: string;
  email: string;
  name: string;
  picture?: string;
  isBetaUser?: boolean; // Added
  apiKey?: string; // Added
  subscriptionStatus?: string; // Add this field
}

// Define a type for Referral Codes
interface ReferralCode {
  code: string;
  status: 'available' | 'used' | 'subscription_paid';
  createdAt: string;
  // link?: string; // If you plan to generate full links
}

interface ApiKeyStatus {
  apiKey: string;
  currentSpendUSD: number;
  maxSpendUSD: string | number; // Can be 'Unlimited' or a number
  isLimitReached: boolean;
  subscriptionStatus: string;
  shouldPromptForSubscription: boolean;
  limitReachedMessage: string | null;
  balanceUSD?: number; // Represents total credits
}

interface TotalUsageData {
  promptTokens: number;
  candidatesTokens: number;
  totalTokens: number;
  apiCalls: number;
  totalCostUSD: number;
}

// Cookie helper functions
const setCookie = (name: string, value: string, days: number) => {
  let expires = "";
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    expires = "; expires=" + date.toUTCString();
  }
  document.cookie = name + "=" + (value || "")  + expires + "; path=/; SameSite=Lax";
};

const getCookie = (name: string): string | null => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  for(let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

// Initialize Stripe Promise
let stripePromise: Promise<Stripe | null> | null = null;

if (STRIPE_PUBLISHABLE_KEY && STRIPE_PUBLISHABLE_KEY.startsWith('pk_')) { // Basic check if key looks valid
  stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
} else {
  console.error(
    "Stripe Publishable Key is not configured correctly or is missing. " +
    "Please check your .env files and the VITE_STRIPE_PUBLISHABLE_KEY_SANDBOX/LIVE variables in src/config.ts, " +
    "or ensure import.meta.env.MODE is set if relying on that for selection."
  );
}

// Main App component that includes dashboard logic
function DashboardApp() {
  const [currentUser, setCurrentUser] = useState<AppUser | null>(null);
  const [, setIsNewUser] = useState<boolean | null>(null); // Value not used but setter is needed
  const [referralCodes, setReferralCodes] = useState<ReferralCode[]>([]); // Added state for referral codes
  const [betaRequestStatus, setBetaRequestStatus] = useState<'idle' | 'pending' | 'sent' | 'error'>('idle');
  const [betaRequestError, setBetaRequestError] = useState<string | null>(null);
  const [betaRequestReasonText, setBetaRequestReasonText] = useState<string>('');
  const [activeReferralCode, setActiveReferralCode] = useState<string | null>(null); // Added state for active referral code

  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessageState] = useState<string | null>(null);
  const [snackbarSeverity, setSnackbarSeverityState] = useState<AlertColor | undefined>('info');

  const [showApiKey, setShowApiKey] = useState(false);

  // New state for API Key Status
  const [apiKeyStatus, setApiKeyStatus] = useState<ApiKeyStatus | null>(null);
  const [checkingApiKeyStatus, setCheckingApiKeyStatus] = useState<boolean>(false);
  const [, setApiKeyStatusError] = useState<string | null>(null); // Value not used but setter is needed

  const [totalLifetimeSpendUSD, setTotalLifetimeSpendUSD] = useState<number | null>(null);
  const [checkingTotalUsage, setCheckingTotalUsage] = useState<boolean>(false);

  // State for "Buy Usage" dialog
  const [showBuyUsageDialog, setShowBuyUsageDialog] = useState(false);
  const [buyUsageDialogMessage, setBuyUsageDialogMessage] = useState('');

  const showSnackbar = useCallback((message: string, severity: AlertColor = 'success') => {
    setSnackbarMessageState(message);
    setSnackbarSeverityState(severity);
    setSnackbarOpen(true);
  }, []);

  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  useEffect(() => {
    if (!currentUser) {
      setBetaRequestStatus('idle');
      setBetaRequestError(null);
      setBetaRequestReasonText('');
      setReferralCodes([]); // Clear referral codes if no user
      setApiKeyStatus(null); // Clear API key status on logout
      setTotalLifetimeSpendUSD(null);
    }
  }, [currentUser]);

  // Restore user from localStorage on app load
  useEffect(() => {
    const storedUser = localStorage.getItem('unitaiUser');
    if (storedUser) {
      const parsedUser: AppUser = JSON.parse(storedUser);
      setCurrentUser(parsedUser);
      // Fetch referral codes if user is loaded from storage
      // Ensure apiKey is available for the fetch if needed by the backend for this endpoint
    }
  }, []);

  // Fetch referral codes when currentUser email is available
  useEffect(() => {
    const fetchReferralCodesByEmail = async () => {
      if (currentUser && currentUser.email) { 
        // console.log('[App.tsx] Attempting to fetch referral codes for user by email:', currentUser.email);
        try {
          // Using the new endpoint that takes email as a path parameter
          const response = await axios.get(`${API_BASE_URL}/referral-codes/by-email/${encodeURIComponent(currentUser.email)}`);
          
          if (Array.isArray(response.data)) {
            setReferralCodes(response.data);
          } else {
            console.warn('[App.tsx] Data for referral codes (by email) is not an array:', response.data);
            setReferralCodes([]); 
          }
        } catch (error) {
          if (axios.isAxiosError(error)) {
            console.error("[App.tsx] Failed to fetch referral codes by email:", error.message);
            if (error.response?.status === 404) {
              // console.log("[App.tsx] No referral codes found for email via new endpoint or user not found.");
            } else {
              // showSnackbar('Could not load your referral codes at this time.', 'error'); // Optional: more generic error
            }
          } else {
            console.error("[App.tsx] Failed to fetch referral codes by email:", error);
          }
          setReferralCodes([]); // Clear codes on any error or 404
        }
      } else {
        setReferralCodes([]); // Clear if no current user or email
      }
    };
    fetchReferralCodesByEmail();
  }, [currentUser?.email]); // Re-fetch if currentUser.email changes

  // Effect to process referral code from URL or cookie on initial load
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const refCodeFromUrl = params.get('c');
    let initialRefCode: string | null = null;

    if (refCodeFromUrl) {
      initialRefCode = refCodeFromUrl;
      setCookie(REFERRAL_COOKIE_NAME, refCodeFromUrl, 30); // Save to cookie for 30 days
      console.log(`[App.tsx] Referral code from URL: ${refCodeFromUrl}, saved to cookie.`);
      // Optional: Clean the URL?
      // window.history.replaceState({}, document.title, window.location.pathname);
    } else {
      const refCodeFromCookie = getCookie(REFERRAL_COOKIE_NAME);
      if (refCodeFromCookie) {
        initialRefCode = refCodeFromCookie;
        console.log(`[App.tsx] Referral code from cookie: ${refCodeFromCookie}`);
      }
    }
    setActiveReferralCode(initialRefCode);
  }, []); // Empty dependency array ensures this runs only once on mount

  // Fetch API Key Status (includes total credits in balanceUSD)
  useEffect(() => {
    const fetchApiKeyStatus = async () => {
      if (currentUser && currentUser.apiKey) {
        setCheckingApiKeyStatus(true);
        try {
          const response = await axios.get<ApiKeyStatus>(
            `${API_BASE_URL}/api-key-status/${currentUser.apiKey}`
          );
          setApiKeyStatus(response.data);
          // Update currentUser with the fetched subscriptionStatus
          if (response.data.subscriptionStatus !== currentUser.subscriptionStatus) {
            setCurrentUser(prevUser => prevUser ? ({ ...prevUser, subscriptionStatus: response.data.subscriptionStatus }) : null);
            // If also storing full user in localStorage, consider updating it here too.
            const storedUserString = localStorage.getItem('unitaiUser');
            if (storedUserString) {
                try {
                    const userFromStorage: AppUser = JSON.parse(storedUserString);
                    if (userFromStorage.id === currentUser.id) { // Ensure it's the same user
                        userFromStorage.subscriptionStatus = response.data.subscriptionStatus;
                        localStorage.setItem('unitaiUser', JSON.stringify(userFromStorage));
                    }
                } catch (e) { console.error("Error updating localStorage user with sub status:", e);}
            }
          }
        } catch (err) {
          console.error("[App.tsx] Failed to fetch API key status:", err);
          let errMsg = "Could not load API key status.";
          if (axios.isAxiosError(err) && err.response) {
            errMsg = err.response.data?.message || err.response.data?.error || errMsg;
          }
          // Potentially show a non-blocking snackbar or just rely on console for this background check
          // showSnackbar('Failed to update API key status.', 'warning');
        }
        setCheckingApiKeyStatus(false);
      }
    };
    if (currentUser?.apiKey) { // Only run if apiKey is present
        fetchApiKeyStatus();
    }
  }, [currentUser?.apiKey]); // Depend only on apiKey to avoid loops with currentUser object itself

  // Fetch Total Lifetime Usage
  useEffect(() => {
    const fetchTotalUsage = async () => {
      if (currentUser && currentUser.apiKey) {
        setCheckingTotalUsage(true);
        try {
          const response = await axios.get<TotalUsageData>(
            `${API_BASE_URL}/${currentUser.apiKey}/usage/total`
          );
          setTotalLifetimeSpendUSD(response.data.totalCostUSD || 0);
        } catch (err) {
          console.error("[App.tsx] Failed to fetch total usage:", err);
          // showSnackbar('Could not load total usage data.', 'error');
          setTotalLifetimeSpendUSD(0); // Default to 0 on error to avoid NaN issues
        }
        setCheckingTotalUsage(false);
      } else {
        setTotalLifetimeSpendUSD(null); // Clear if no API key
      }
    };
    if (currentUser?.apiKey) {
      fetchTotalUsage();
    }
  }, [currentUser?.apiKey]);

  const calculatedNetBalance = React.useMemo(() => {
    if (apiKeyStatus?.balanceUSD === undefined || totalLifetimeSpendUSD === null) {
      return undefined; // Or null, to indicate loading/not available
    }
    return (apiKeyStatus.balanceUSD || 0) - (totalLifetimeSpendUSD || 0);
  }, [apiKeyStatus?.balanceUSD, totalLifetimeSpendUSD]);

  // Function to be called from elsewhere (e.g., API error handler) to open the dialog
  // Commented out - not currently used but may be needed for future API error handling
  // const openBuyUsageDialog = useCallback(() => {
  //   const netBalance = calculatedNetBalance;
  //   let message = 'Your account balance is low. Purchase $50 usage credit to continue.';
  //   if (netBalance !== undefined) {
  //     message = `Your current net balance is $${netBalance.toFixed(2)}. Purchase $50 usage credit to continue.`;
  //   }
  //   setBuyUsageDialogMessage(message);
  //   setShowBuyUsageDialog(true);
  // }, [calculatedNetBalance]);

  const handleLoginSuccess = (userData: AppUser, newUserStatus: boolean) => {
    setCurrentUser(userData);
    localStorage.setItem('unitaiUser', JSON.stringify(userData));
    if (newUserStatus) {
      showSnackbar(`Welcome new user, ${userData.name || 'friend'}!`, 'info');
    } else {
      showSnackbar(`Welcome back, ${userData.name || 'friend'}!`);
    }
    setBetaRequestStatus('idle');
    setBetaRequestError(null);
    setBetaRequestReasonText('');
  };

  const handleLoginError = (errorMessage?: string) => {
    setCurrentUser(null);
    setReferralCodes([]); // Clear referral codes on login error
    showSnackbar(errorMessage || 'Login failed. Please try again.', 'error');
    setBetaRequestStatus('idle');
    setBetaRequestError(null);
    setBetaRequestReasonText('');
  };

  const handleSignOut = () => {
    googleLogout();
    setCurrentUser(null);
    localStorage.removeItem('unitaiUser');
    setReferralCodes([]); // Clear referral codes on sign out
    showSnackbar('You have been signed out.', 'info');
    console.log("User signed out.");
    // TODO: Optionally call backend logout endpoint here
    // axios.post('/api/auth/logout').then(...);
  };

  const handleRequestBetaAccess = async () => {
    if (!currentUser) return;

    if (!betaRequestReasonText.trim()) {
      showSnackbar('Please provide a reason for your beta access request.', 'warning');
      setBetaRequestError('Reason is required.');
      return;
    }

    setBetaRequestStatus('pending');
    setBetaRequestError(null);
    try {
      // IMPORTANT: Backend expects userId and betaRequestReason
      const response = await axios.post(BETA_REQUEST_URL, { 
        userId: currentUser.id, 
        betaRequestReason: betaRequestReasonText.trim()
      });
      setBetaRequestStatus('sent');
      showSnackbar(response.data.message || 'Beta request submitted!', 'info');
    } catch (error) {
      console.error("Beta access request failed:", error);
      let errMsg = "Failed to submit beta request.";
      if (axios.isAxiosError(error) && error.response) {
        errMsg = error.response.data?.message || errMsg;
      }
      setBetaRequestError(errMsg);
      setBetaRequestStatus('error');
      showSnackbar(`Error: ${errMsg}`, 'error');
    }
  };

  // Helper to mask API key except first/last 5 chars
  const maskApiKey = (key: string) => {
    if (!key || key.length <= 10) return key;
    return key.slice(0, 5) + '*'.repeat(key.length - 10) + key.slice(-5);
  };

  const handleBuyUsageClick = async () => {
    if (!currentUser || !currentUser.apiKey || !stripePromise) {
      showSnackbar('Cannot initiate purchase. User or API key missing, or Stripe is not configured.', 'error');
      setShowBuyUsageDialog(false);
      return;
    }
    showSnackbar('Redirecting to purchase page...', 'info');
    setShowBuyUsageDialog(false);
    try {
      const response = await axios.post<{ sessionId: string }>(
        `${API_BASE_URL}/stripe/create-onetime-usage-checkout-session`,
        {},
        { headers: { 'x-api-key': currentUser.apiKey } }
      );
      const { sessionId } = response.data;
      const stripe = await stripePromise;
      if (stripe && sessionId) {
        const { error } = await stripe.redirectToCheckout({ sessionId });
        if (error) {
          console.error("Stripe checkout redirection error (one-time usage):", error);
          showSnackbar(error.message || 'Failed to redirect to Stripe for one-time purchase.', 'error');
        }
      } else {
        showSnackbar('Stripe.js has not loaded or session ID missing for one-time purchase.', 'error');
      }
    } catch (err) {
      console.error("Error creating Stripe one-time usage checkout session:", err);
      let errMsg = "Could not initiate one-time purchase. Please try again.";
      if (axios.isAxiosError(err) && err.response) {
        errMsg = err.response.data?.message || err.response.data?.error || errMsg;
      }
      showSnackbar(errMsg, 'error');
    }
  };

  const handleSubscribeClick = async () => {
    if (!currentUser || !currentUser.apiKey || !stripePromise) {
      showSnackbar('Cannot initiate subscription. User or API key missing, or Stripe is not configured.', 'error');
      return;
    }
    showSnackbar('Initiating subscription...', 'info');
    try {
      const response = await axios.post<{ sessionId: string }>(
        `${API_BASE_URL}/stripe/create-checkout-session`,
        {},
        { headers: { 'x-api-key': currentUser.apiKey } }
      );
      const { sessionId } = response.data;
      const stripe = await stripePromise;
      if (stripe && sessionId) {
        const { error } = await stripe.redirectToCheckout({ sessionId });
        if (error) {
          console.error("Stripe checkout redirection error:", error);
          showSnackbar(error.message || 'Failed to redirect to Stripe Checkout.', 'error');
        }
      } else if (!stripe) {
        showSnackbar('Stripe.js has not loaded. Cannot redirect to checkout.', 'error');
      } else if (!sessionId) {
        showSnackbar('Could not retrieve a checkout session ID from the server.', 'error');
      }
    } catch (err) {
      console.error("Error creating Stripe checkout session:", err);
      let errMsg = "Could not initiate subscription. Please try again.";
      if (axios.isAxiosError(err) && err.response) {
        errMsg = err.response.data?.message || err.response.data?.error || errMsg;
      }
      showSnackbar(errMsg, 'error');
    }
  };

  const handleManageSubscription = async () => {
    if (!currentUser || !currentUser.apiKey || !stripePromise) {
      showSnackbar('Cannot initiate subscription. User or API key missing, or Stripe is not configured.', 'error');
      return;
    }
    showSnackbar('Initiating subscription...', 'info');
    try {
      const response = await axios.post<{ sessionId: string }>(
        `${API_BASE_URL}/stripe/create-checkout-session`,
        {},
        { headers: { 'x-api-key': currentUser.apiKey } }
      );
      const { sessionId } = response.data;
      const stripe = await stripePromise;
      if (stripe && sessionId) {
        const { error } = await stripe.redirectToCheckout({ sessionId });
        if (error) {
          console.error("Stripe checkout redirection error:", error);
          showSnackbar(error.message || 'Failed to redirect to Stripe Checkout.', 'error');
        }
      } else if (!stripe) {
        showSnackbar('Stripe.js has not loaded. Cannot redirect to checkout.', 'error');
      } else if (!sessionId) {
        showSnackbar('Could not retrieve a checkout session ID from the server.', 'error');
      }
    } catch (err) {
      console.error("Error creating Stripe checkout session:", err);
      let errMsg = "Could not initiate subscription. Please try again.";
      if (axios.isAxiosError(err) && err.response) {
        errMsg = err.response.data?.message || err.response.data?.error || errMsg;
      }
      showSnackbar(errMsg, 'error');
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Box component="header" sx={{ padding: 2, borderBottom: '1px solid #ddd' }}>
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}> { /* Group logo and text */}
            <img src="/logo.png" alt="UnitAI Logo" style={{ height: '60px', marginRight: '10px' }} />
            <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
              Dashboard&nbsp;
            </Typography>
            <Link href="https://unitai.net" target="_blank" rel="noopener noreferrer" sx={{ textDecoration: 'none', color: 'inherit' }}>
              <Typography variant="h5" component="span" sx={{ fontWeight: 'bold', textDecoration: 'underline' }}>
                UnitAI
              </Typography>
            </Link>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}> {/* Container for right-aligned items, added gap */}
            <Button 
              variant="outlined" 
              color="info" 
              onClick={() => window.open('https://t.me/+U72zsIW57fYyMzRi', '_blank')}
              sx={{ ml: 1 }} // Added margin to separate from other elements if any appear to its left
            >
              Support
            </Button>
            {currentUser && (calculatedNetBalance !== undefined) && (
              <Chip 
                label={`Net Balance: $${calculatedNetBalance.toFixed(2)}`}
                color={calculatedNetBalance >= 0 ? "success" : "error"}
                variant="outlined"
                sx={{ fontWeight: 'bold' }}
              />
            )}
            {currentUser && (checkingApiKeyStatus || checkingTotalUsage) && calculatedNetBalance === undefined && (
                 <Chip label="Net Balance: Loading..." variant="outlined" />
            )}
            {currentUser && (
              <SubscriptionStatusDisplay 
                currentUser={currentUser} 
                onManageSubscription={handleManageSubscription} 
              />
            )}
            {/* {currentUser && <TotalUsageDisplay currentUser={currentUser} />} */}{/* Remove or comment out TotalUsageDisplay */}
          </Box>
        </Toolbar>
      </Box>

      <Container component="main" sx={{ 
        marginTop: 4, 
        marginBottom: 4, 
        flexGrow: 1, 
        textAlign: 'center',
        px: { xs: 2, sm: 3 }
      }}>
        {!currentUser ? (
          <>
            <Box sx={{ marginBottom: 3 }}>
              <Typography variant="h5" gutterBottom>
                Login to access closed beta and earn $17:
              </Typography>
              <Divider sx={{ marginY: 2 }} />
            </Box>
            <Box display="flex" justifyContent="center" sx={{ width: '100%' }}>
              <Box sx={{
                width: { xs: '100%', sm: 2/3, md: 1/2, lg: 1/3 },
                maxWidth: '600px'
              }}>
                <LoginButton 
                  onLoginSuccess={handleLoginSuccess} 
                  onLoginError={handleLoginError} 
                  activeReferralCode={activeReferralCode}
                />
              </Box>
            </Box>
          </>
        ) : (
          <Box sx={{textAlign: 'left'}}>
            {apiKeyStatus && apiKeyStatus.shouldPromptForSubscription && calculatedNetBalance !== undefined && calculatedNetBalance <=0 && !currentUser.subscriptionStatus && (
              <Paper elevation={3} sx={{ padding: 2, marginY: 2, backgroundColor: 'warning.light' }}>
                <Typography variant="h6" gutterBottom color="warning.contrastText">
                  Free Tier Limit Reached
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }} color="warning.contrastText">
                  {apiKeyStatus.limitReachedMessage || 'You have reached your API usage limit for the free tier.'}
                </Typography>
                <Button variant="contained" color="primary" onClick={handleSubscribeClick}>
                  Subscribe for More Usage
                </Button>
              </Paper>
            )}

            {currentUser.isBetaUser ? (
              <Box display="flex" justifyContent="center" sx={{ my: 2 }}>
                <MUIAlert severity="success" sx={{ maxWidth: 500, width: '100%' }}>
                  <AlertTitle>Beta Access Granted!</AlertTitle>
                  Your participation is approved! Explore the features.
                </MUIAlert>
              </Box>
            ) : (
              <Paper elevation={2} sx={{ padding: 2, marginY: 2}}>
                <Typography variant="h6" gutterBottom>Closed Beta Program</Typography>
                {betaRequestStatus === 'idle' && (
                  <Box component="form" noValidate autoComplete="off">
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Why do you want to join the closed beta?"
                      variant="outlined"
                      value={betaRequestReasonText}
                      onChange={(e) => setBetaRequestReasonText(e.target.value)}
                      sx={{ marginBottom: 2 }}
                    />
                    <Button variant="contained" color="primary" onClick={handleRequestBetaAccess}>
                      Ask to Join Closed BETA
                    </Button>
                  </Box>
                )}
                {betaRequestStatus === 'pending' && <CircularProgress />}
                {betaRequestStatus === 'sent' && 
                    <MUIAlert severity="info" sx={{mt:1}}>Beta request sent. We'll notify you upon approval!</MUIAlert>}
                {betaRequestStatus === 'error' && 
                    <MUIAlert severity="error" sx={{mt:1}}>{betaRequestError || 'Could not send beta request.'}</MUIAlert>}
              </Paper>
            )}

            <Paper elevation={1} sx={{ padding: 2, marginTop: 1, textAlign: 'left' }}>
              <Typography variant="h6">User Details:</Typography>
              <Typography>Name: {currentUser.name}</Typography>
              <Typography>Email: {currentUser.email}</Typography>
              {currentUser.isBetaUser && currentUser.apiKey && (() => {
                const apiKeyStr = currentUser.apiKey!;
                return (
                  <Box sx={{ mt: 1 }}>
                    <Typography sx={{ fontWeight: 500 }}>API Key:</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TextField
                        value={showApiKey ? apiKeyStr : maskApiKey(apiKeyStr)}
                        variant="outlined"
                        size="small"
                        InputProps={{
                          readOnly: true,
                          sx: { fontFamily: 'monospace', letterSpacing: 1 },
                          endAdornment: (
                            <>
                              <IconButton onClick={() => setShowApiKey((v) => !v)} size="small" aria-label="Toggle API key visibility">
                                {showApiKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                              </IconButton>
                              <IconButton
                                onClick={async () => {
                                  await navigator.clipboard.writeText(apiKeyStr);
                                  showSnackbar('API key copied!', 'success');
                                }}
                                size="small"
                                aria-label="Copy API key"
                              >
                                <ContentCopyIcon />
                              </IconButton>
                            </>
                          ),
                        }}
                        sx={{ width: { xs: '100%', sm: 350 }, fontFamily: 'monospace' }}
                      />
                    </Box>
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 'medium' }}>
                        Unity Client Installation:
                      </Typography>
                      <Typography variant="body1" component="div">
                        <ol>
                          <li>
                            <strong>Via Unity Package Manager (UPM):</strong>
                            <ul>
                              <li>Open Unity.</li>
                              <li>Go to <strong>Window &gt; Package Manager</strong>.</li>
                              <li>Click the <strong>"+"</strong> button and select <strong>"Add package from git URL..."</strong></li>
                              <li>Enter the GitHub URL with the correct path and tag, e.g.:
                                <Paper elevation={1} sx={{ my: 1, p: 1, backgroundColor: 'grey.100', overflowX: 'auto' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all', flexGrow: 1 }}>
                                      {'https://github.com/oleksiivinogradov/unitai_upm.git?path=unitai_upm#latest'}
                                    </pre>
                                    <IconButton
                                      onClick={async () => {
                                        await navigator.clipboard.writeText('https://github.com/oleksiivinogradov/unitai_upm.git?path=unitai_upm#latest');
                                        showSnackbar('Git URL copied!', 'success');
                                      }}
                                      size="small"
                                      aria-label="Copy Git URL"
                                    >
                                      <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                </Paper>
                              </li>
                            </ul>
                          </li>
                        </ol>
                      </Typography>
                    </Box>
                  </Box>
                );
              })()}
              <ReferralLinks 
                referralCodes={referralCodes} 
                currentUser={currentUser}
                showSnackbar={showSnackbar} 
                sx={{ mt: 2 }}
              />
            </Paper>
            
            {currentUser.apiKey && (
                <UsageDisplay currentUser={currentUser} showSnackbar={showSnackbar} />
            )}

          </Box>
        )}
      </Container>

      <Box
        component="footer"
        sx={{
          width: '100%',
          padding: 2,
          marginTop: 'auto',
          backgroundColor: (theme) =>
            theme.palette.mode === 'light'
              ? theme.palette.grey[200]
              : theme.palette.grey[800],
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        {currentUser && (
          <Button variant="outlined" color="secondary" onClick={handleSignOut} sx={{ marginBottom: 1 }}>
            Sign Out
          </Button>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
          © {new Date().getFullYear()} UnitAI Project
        </Typography>
      </Box>
      <NotificationSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        handleClose={handleSnackbarClose}
      />
      {/* Buy Usage Dialog - message is now set by openBuyUsageDialog */}
      <Dialog open={showBuyUsageDialog} onClose={() => setShowBuyUsageDialog(false)} aria-labelledby="buy-usage-dialog-title">
        <DialogTitle id="buy-usage-dialog-title">Insufficient Balance</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {buyUsageDialogMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowBuyUsageDialog(false)}>Cancel</Button>
          <Button onClick={handleBuyUsageClick} variant="contained" color="primary" autoFocus>
            Buy $50 Usage Credit
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// New top-level App component for routing
function App() {
  return (
    <Router>
      <AnalyticsTracker />
      <Routes>
        <Route path="/payment-success" element={<PaymentSuccessPage />} />
        <Route path="/payment-cancelled" element={<PaymentCancelledPage />} />
        <Route path="/*" element={<DashboardApp />} /> {/* Default route to your main app logic */}
      </Routes>
    </Router>
  );
}

export default App;
